/**
 * PnjLogo — an inline SVG monogram badge for "PNJ" (Politeknik Negeri
 * Jakarta), used as a "Created by PNJ" credit throughout the app.
 *
 * Design language matches the MRT / Shunting Alerts theme:
 *   - Deep-navy backdrop, cyan → green gradient stroke.
 *   - Softly glowing orbit ring to hint at signal/alarm.
 *   - Monospaced tabular numerals + display serif for the 'PNJ' mark.
 */
export default function PnjLogo({ size = 40, className = "", withLabel = true }) {
  const uid = `pnj-${size}`;
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="PNJ · Created by C. Panji"
        role="img"
        className="drop-shadow-[0_0_10px_rgba(14,165,233,0.35)]"
      >
        <defs>
          <linearGradient id={`${uid}-ring`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="55%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
          <linearGradient id={`${uid}-face`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0c1b2a" />
            <stop offset="100%" stopColor="#062036" />
          </linearGradient>
          <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* soft outer glow */}
        <circle cx="32" cy="32" r="30" fill={`url(#${uid}-glow)`} />

        {/* main disc */}
        <circle
          cx="32"
          cy="32"
          r="26"
          fill={`url(#${uid}-face)`}
          stroke={`url(#${uid}-ring)`}
          strokeWidth="2.2"
        />

        {/* orbit ticks (compass-like) */}
        <g stroke="#0ea5e9" strokeWidth="1.4" strokeLinecap="round" opacity="0.55">
          <line x1="32" y1="4" x2="32" y2="8" />
          <line x1="32" y1="56" x2="32" y2="60" />
          <line x1="4" y1="32" x2="8" y2="32" />
          <line x1="56" y1="32" x2="60" y2="32" />
        </g>

        {/* accent arc — evokes a track / signal path */}
        <path
          d="M14 40 A 20 20 0 0 0 50 40"
          fill="none"
          stroke="#22c55e"
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.75"
        />

        {/* PNJ wordmark */}
        <text
          x="32"
          y="38"
          textAnchor="middle"
          fontFamily="Manrope, 'IBM Plex Sans', sans-serif"
          fontWeight="800"
          fontSize="18"
          letterSpacing="1.2"
          fill="#e2f5ff"
        >
          PNJ
        </text>
      </svg>

      {withLabel && (
        <div className="leading-tight">
          <div className="text-[9px] uppercase tracking-[0.3em] text-slate-400">
            Created by
          </div>
          <div className="text-xs font-display font-semibold text-[#7dd3fc]">
            C. Panji
          </div>
        </div>
      )}
    </div>
  );
}
